import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAuditStats } from "../api/audit";
import "./security-dashboard.css";

export const SecurityDashboard = (): JSX.Element => {
    const [daysRange, setDaysRange] = useState(7);

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysRange);
    const startDateStr = startDate.toISOString();
    const endDateStr = new Date().toISOString();

    const { data: stats, error, isLoading } = useQuery({
        queryKey: ["audit", "stats", startDateStr, endDateStr],
        queryFn: () => fetchAuditStats(startDateStr, endDateStr),
        staleTime: 60 * 1000, // 1 minute
    });

    if (error) {
        return (
            <div className="security-dashboard">
                <h2>Security Dashboard</h2>
                <div className="security-dashboard__error">
                    Error loading security statistics. Please try again.
                </div>
            </div>
        );
    }

    // Calculate security metrics from stats
    const loginSuccessCount = stats?.eventsByType["auth.login.success"] || 0;
    const loginFailedCount = stats?.eventsByType["auth.login.failed"] || 0;
    const passwordResetRequestCount =
        stats?.eventsByType["auth.password_reset.requested"] || 0;
    const passwordResetSuccessCount =
        stats?.eventsByType["auth.password_reset.confirmed"] || 0;
    const totpEnabledCount = stats?.eventsByType["auth.totp.enabled"] || 0;

    // Get top event types
    const topEventTypes = stats
        ? Object.entries(stats.eventsByType)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10)
        : [];

    // Get top actors
    const topActors = stats
        ? Object.entries(stats.eventsByActor)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10)
        : [];

    return (
        <div className="security-dashboard">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                <h2>Security Dashboard</h2>
                <select
                    value={daysRange}
                    onChange={(e) => setDaysRange(parseInt(e.target.value, 10))}
                    style={{
                        padding: "0.5rem",
                        background: "rgba(15, 23, 42, 0.8)",
                        border: "1px solid rgba(148, 163, 184, 0.2)",
                        borderRadius: "0.375rem",
                        color: "#f8fafc",
                        fontSize: "0.875rem",
                    }}
                >
                    <option value={1}>Last 24 hours</option>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
            </div>

            {isLoading ? (
                <div className="security-dashboard__loading">
                    Loading security statistics...
                </div>
            ) : (
                <>
                    <div className="security-stats">
                        <div className="security-stat-card">
                            <p className="security-stat-card__label">Total Events</p>
                            <p className="security-stat-card__value">
                                {stats?.totalEvents || 0}
                            </p>
                        </div>

                        <div className="security-stat-card">
                            <p className="security-stat-card__label">Successful Logins</p>
                            <p className="security-stat-card__value security-stat-card__value--success">
                                {loginSuccessCount}
                            </p>
                        </div>

                        <div className="security-stat-card">
                            <p className="security-stat-card__label">Failed Logins</p>
                            <p className="security-stat-card__value security-stat-card__value--warning">
                                {loginFailedCount}
                            </p>
                        </div>

                        <div className="security-stat-card">
                            <p className="security-stat-card__label">Password Resets</p>
                            <p className="security-stat-card__value">
                                {passwordResetSuccessCount} / {passwordResetRequestCount}
                            </p>
                        </div>

                        <div className="security-stat-card">
                            <p className="security-stat-card__label">TOTP Enabled</p>
                            <p className="security-stat-card__value security-stat-card__value--success">
                                {totpEnabledCount}
                            </p>
                        </div>
                    </div>

                    <div className="security-events">
                        <h3>Security Events by Type</h3>
                        <div className="security-events__list">
                            {topEventTypes.length > 0 ? (
                                topEventTypes.map(([eventType, count]) => {
                                    let className = "security-event-item";
                                    if (eventType.includes("success") || eventType.includes("enabled") || eventType.includes("confirmed")) {
                                        className += " security-event-item--success";
                                    } else if (eventType.includes("failed") || eventType.includes("lockout") || eventType.includes("rate_limited")) {
                                        className += " security-event-item--danger";
                                    } else {
                                        className += " security-event-item--warning";
                                    }

                                    return (
                                        <div key={eventType} className={className}>
                                            <span className="security-event-item__type">{eventType}</span>
                                            <span className="security-event-item__count">{count}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <p style={{ color: "rgba(226, 232, 240, 0.7)", margin: 0 }}>
                                    No events in this period
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="security-charts">
                        {topActors.length > 0 && (
                            <div className="security-chart">
                                <h3>Top Actors</h3>
                                <ul className="security-chart__list">
                                    {topActors.map(([actorId, count]) => (
                                        <li key={actorId} className="security-chart__item">
                                            <span className="security-chart__label">
                                                {actorId.slice(0, 8)}...
                                            </span>
                                            <span className="security-chart__value">{count}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {stats && Object.keys(stats.eventsByTeam).length > 0 && (
                            <div className="security-chart">
                                <h3>Events by Team</h3>
                                <ul className="security-chart__list">
                                    {Object.entries(stats.eventsByTeam)
                                        .sort(([, a], [, b]) => b - a)
                                        .slice(0, 10)
                                        .map(([teamId, count]) => (
                                            <li key={teamId} className="security-chart__item">
                                                <span className="security-chart__label">
                                                    {teamId.slice(0, 8)}...
                                                </span>
                                                <span className="security-chart__value">{count}</span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};